import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification, deleteUser, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';
import { generateHostId } from '../utils/hostAchievements';

const SignUpPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resendPassword, setResendPassword] = useState('');

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    let userCredential = null;

    try {
      // Create user account
      userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user, {
        url: `${window.location.origin}/verify-initial?uid=${user.uid}`,
        handleCodeInApp: false
      });

      // Generate sequential host ID for new hosts
      const newHostId = await generateHostId(db);

      // Create user document with email verification status
      await setDoc(doc(db, 'users', user.uid), {
        fullName,
        email,
        role: 'host', // default role
        plan: 'host', // default plan for new users
        hostId: newHostId, // Assign sequential host ID
        emailVerified: false, // Email not verified yet
        createdAt: serverTimestamp(),
        emailVerificationSentAt: serverTimestamp(),
      });

      // Sign out the user until they verify their email
      await auth.signOut();

      setUserEmail(email);
      setSignupSuccess(true);
    } catch (err) {
      console.error('Sign up error:', err);
      
      // Cleanup: If auth account was created but Firestore failed, delete the auth account
      if (userCredential && userCredential.user) {
        try {
          console.log('🧹 Cleaning up failed signup - deleting auth account');
          await deleteUser(userCredential.user);
          console.log('✅ Auth account cleaned up successfully');
        } catch (cleanupError) {
          console.error('❌ Failed to cleanup auth account:', cleanupError);
        }
      }
      
      // Handle specific Firebase Auth errors
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead, or use a different email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters long.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    setIsLoading(true);

    try {
      // Sign in the existing user first
      const userCredential = await signInWithEmailAndPassword(auth, userEmail, resendPassword);
      const user = userCredential.user;
      
      // Check if email is already verified
      if (user.emailVerified) {
        setError('Your email is already verified. You can sign in normally.');
        await auth.signOut();
        return;
      }
      
      // Resend verification email
      await sendEmailVerification(user, {
        url: `${window.location.origin}/verify-initial?uid=${user.uid}`,
        handleCodeInApp: false
      });
      
      // Update the verification sent timestamp
      await setDoc(doc(db, 'users', user.uid), {
        emailVerificationSentAt: serverTimestamp(),
      }, { merge: true });

      // Sign out again
      await auth.signOut();
      
      setError('');
      alert('Verification email sent! Please check your inbox.');
    } catch (err) {
      console.error('Resend verification error:', err);
      
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address. Please sign up first.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact support.');
      } else {
        setError('Failed to resend verification email. Please check your password and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (signupSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
        <div className="absolute top-4 left-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-green-400 hover:text-green-300 font-semibold transition-colors"
          >
            ← Back to Landing Page
          </button>
        </div>

        <div className="w-full max-w-md bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
          <div className="text-6xl mb-4">📧</div>
          <h1 className="text-2xl font-bold text-white mb-4">Check Your Email!</h1>
          <p className="text-gray-300 mb-6">
            We've sent a verification email to <strong className="text-green-400">{userEmail}</strong>
          </p>
          <p className="text-gray-400 text-sm mb-6">
            Please click the verification link in your email to activate your account and start using Trivia Plug.
          </p>
          
          {error && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-green-200 font-semibold mb-2">Password</label>
              <input
                type="password"
                className="w-full p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none transition"
                value={resendPassword}
                onChange={(e) => setResendPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
              />
              <p className="text-gray-400 text-xs mt-1">
                We need your password to verify your identity before resending the email.
              </p>
            </div>
            
            <button
              onClick={handleResendVerification}
              disabled={isLoading || !resendPassword}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-bold py-3 rounded-lg shadow-lg transition"
            >
              {isLoading ? 'Sending...' : 'Resend Verification Email'}
            </button>
            
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 rounded-lg shadow-lg transition"
            >
              Go to Sign In
            </button>
            
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                Don't remember your password?{' '}
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="text-green-400 hover:text-green-300 font-semibold transition-colors"
                >
                  Reset it here
                </button>
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-green-400 font-semibold mb-2">What happens next?</h3>
            <ul className="text-gray-300 text-sm space-y-1 text-left">
              <li>• Check your email inbox (and spam folder)</li>
              <li>• Click the verification link in the email</li>
              <li>• Your account will be activated automatically</li>
              <li>• You can then sign in and start using Trivia Plug</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
      {/* Back to Landing Page Button */}
      <div className="absolute top-4 left-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-green-400 hover:text-green-300 font-semibold transition-colors"
        >
          ← Back to Landing Page
        </button>
      </div>

      <div className="flex flex-col items-center mb-8 animate-fadein">
        <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
        <p className="text-green-200 mb-2">Join Trivia Plug to buy, sell, host or play</p>
        <p className="text-gray-400 text-sm text-center max-w-md">
          You'll need to verify your email address before you can start using your account.
        </p>
      </div>
      <form
        onSubmit={handleSignUp}
        className="w-full max-w-sm bg-gray-800 rounded-2xl shadow-2xl p-8 flex flex-col gap-4 animate-fadein"
        style={{ animationDelay: '0.2s' }}
      >
        <label className="text-green-200 font-semibold">Full Name</label>
        <input
          type="text"
          className="p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none transition"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          disabled={isLoading}
        />
        <label className="text-green-200 font-semibold">Email</label>
        <input
          type="email"
          className="p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none transition"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        <label className="text-green-200 font-semibold">Password</label>
        <input
          type="password"
          className="p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none transition"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
        />
        {error && <div className="text-red-400 text-sm text-center mt-2">{error}</div>}
        <button
          type="submit"
          disabled={isLoading}
          className="mt-4 bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white font-bold py-3 rounded-lg shadow-lg transition"
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </button>
        
        {/* Sign In Link */}
        <div className="text-center mt-4">
          <p className="text-gray-400 text-sm">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-green-400 hover:text-green-300 font-semibold transition-colors"
            >
              Sign in here
            </button>
          </p>
        </div>
        
        {/* Terms and Privacy */}
        <div className="text-center mt-4 pt-4 border-t border-gray-700">
          <p className="text-gray-500 text-xs">
            By creating an account, you agree to our{' '}
            <a href="/terms" className="text-blue-400 hover:text-blue-300 underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</a>
          </p>
        </div>
      </form>
      <style>{`
        @keyframes fadein {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: none; }
        }
        .animate-fadein {
          animation: fadein 1s cubic-bezier(0.4,0,0.2,1) both;
        }
      `}</style>
    </div>
  );
};

export default SignUpPage;
